// ============================================================
// xFreeScan by XCloud Solutions - Dashboard Page (Light Theme)
// ============================================================
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../../lib/api';
import {
  Shield, Scan, AlertTriangle, TrendingUp,
  ArrowRight, BarChart3, ChevronRight, Clock, Target
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      if (response.success) setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  const statCards = [
    { label: 'Escaneos Totales', value: stats?.total_scans || 0, icon: Scan, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Hallazgos Totales', value: stats?.total_findings || 0, icon: BarChart3, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Hallazgos Criticos', value: stats?.critical_findings || 0, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Score Promedio', value: `${stats?.avg_security_score || 0}/100`, icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700 p-6 shadow-lg shadow-purple-500/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Panel de Seguridad</h1>
            <p className="mt-1 text-purple-200">
              Vista general del estado de seguridad de sus activos digitales.
            </p>
          </div>
          <Link to="/app/scans/new" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-purple-700 shadow transition-all hover:bg-purple-50 whitespace-nowrap">
            <Scan className="h-5 w-5" />
            Nuevo Escaneo
          </Link>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div key={card.label} className="card">
            <div className="flex items-center justify-between">
              <div className={`rounded-xl ${card.bg} p-3`}>
                <card.icon className={`h-6 w-6 ${card.color}`} />
              </div>
              <TrendingUp className="h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-4 text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="mt-1 text-sm text-gray-500">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Severity distribution */}
      {stats?.severity_distribution && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribucion de Hallazgos por Severidad</h3>
          <div className="grid grid-cols-5 gap-4">
            {[
              { key: 'critical', label: 'Critico', color: 'bg-red-500', textColor: 'text-red-600' },
              { key: 'high', label: 'Alto', color: 'bg-orange-500', textColor: 'text-orange-600' },
              { key: 'medium', label: 'Medio', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
              { key: 'low', label: 'Bajo', color: 'bg-blue-500', textColor: 'text-blue-600' },
              { key: 'info', label: 'Info', color: 'bg-gray-400', textColor: 'text-gray-500' },
            ].map(({ key, label, color, textColor }) => {
              const count = stats.severity_distribution[key] || 0;
              const total = stats.total_findings || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={key} className="text-center">
                  <p className={`text-2xl font-bold ${textColor}`}>{count}</p>
                  <p className="text-xs text-gray-500 mt-1">{label}</p>
                  <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Score Trend Chart */}
      {stats?.score_trend?.length > 1 && (() => {
        // Group by target and prepare chart data
        const targets = [...new Set(stats.score_trend.map((s: any) => s.target))] as string[];
        const colors = ['#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#ec4899'];

        // Build unified timeline data
        const allDates = [...new Set(stats.score_trend.map((s: any) => new Date(s.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })))] as string[];
        const chartData = allDates.map((date: string) => {
          const point: any = { date };
          for (const target of targets) {
            const scan = stats.score_trend.find((s: any) =>
              s.target === target && new Date(s.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }) === date
            );
            if (scan) point[target] = scan.score;
          }
          return point;
        });

        return (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Evolucion del Score de Seguridad</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px' }}
                  labelStyle={{ color: '#d1d5db', marginBottom: '4px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                {targets.map((target: string, i: number) => (
                  <Line
                    key={target}
                    type="monotone"
                    dataKey={target}
                    stroke={colors[i % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4, fill: colors[i % colors.length] }}
                    activeDot={{ r: 6 }}
                    name={target.length > 25 ? target.substring(0, 25) + '...' : target}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Score 0-100 (mayor es mejor). Basado en los ultimos {stats.score_trend.length} escaneos completados.
            </p>
          </div>
        );
      })()}

      {/* Recent scans */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Escaneos Recientes</h3>
          <Link to="/app/scans" className="text-sm text-purple-600 hover:text-purple-500 flex items-center gap-1 font-medium">
            Ver todos <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {stats?.recent_scans?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_scans.map((scan: any) => (
              <Link
                key={scan.id}
                to={`/app/scans/${scan.id}`}
                className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-purple-300 hover:shadow-md"
              >
                <div className={`rounded-lg p-2 ${
                  scan.status === 'completed' ? 'bg-emerald-50' :
                  scan.status === 'running' ? 'bg-purple-50' :
                  scan.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                }`}>
                  <Target className={`h-5 w-5 ${
                    scan.status === 'completed' ? 'text-emerald-600' :
                    scan.status === 'running' ? 'text-purple-600' :
                    scan.status === 'failed' ? 'text-red-600' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{scan.target}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`badge ${
                      scan.status === 'completed' ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' :
                      scan.status === 'running' ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200' :
                      scan.status === 'failed' ? 'bg-red-100 text-red-700 ring-1 ring-red-200' : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                    }`}>
                      {scan.status === 'completed' ? 'Completado' :
                       scan.status === 'running' ? 'En progreso' :
                       scan.status === 'failed' ? 'Fallido' : 'En cola'}
                    </span>
                    {scan.grade && (
                      <span className={`text-sm font-bold ${
                        scan.grade.startsWith('A') ? 'grade-a' :
                        scan.grade.startsWith('B') ? 'grade-b' :
                        scan.grade.startsWith('C') ? 'grade-c' :
                        scan.grade.startsWith('D') ? 'grade-d' : 'grade-f'
                      }`}>
                        {scan.grade}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(scan.created_at).toLocaleDateString('es')}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Scan className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-4 text-gray-500">No hay escaneos aun</p>
            <Link to="/app/scans/new" className="btn-primary mt-4 inline-flex">
              Iniciar primer escaneo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
